/**
 * Profile-dropdown "Meta Ads Account" picker (#4173 Chunk 4), 2-step since #5977.
 *
 * Mirrors the GoogleAdAccountSelectionModal in shape — dialog, account list, click-to-switch — but
 * scoped to Meta. Reads from ``metaIntegrationStatusQueryOptions`` for the available ad accounts and
 * the currently-selected one; switches via ``useSelectMetaAdAccountMutation`` (which invalidates the
 * status query so the active selection re-renders here and on the Settings → Integrations card).
 *
 * Step 1 picks the ad account (CONTINUE advances); step 2 configures the account's Facebook Page +
 * Meta Pixel (BACK returns, CONTINUE closes). Selections persist immediately on change — the steps
 * organise the flow, they don't stage writes — so closing early loses nothing, and the wizard's
 * account-setup gate (#4652) still enforces Page + Pixel before any campaign is created.
 *
 * Empty states route the user to ``/settings/integrations`` — there's no "refresh accounts" button
 * here (unlike Google's modal); Meta re-syncs the account list inside the integration modal where
 * the rest of the Meta connection is managed.
 */

import { EmptyState } from '@/components/glitch-ui/empty-state';
import { Spinner } from '@/components/glitch-ui/spinner';
import { useEffect, useState } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, ExternalLink } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MetaAssetSelect } from '@/components/glitch-ui/meta-asset-select';
import { useToast } from '@/hooks/useToast';
import {
  metaIntegrationStatusQueryOptions,
  metaPagesQueryOptions,
  metaPixelsQueryOptions,
  useSelectMetaAdAccountMutation,
} from '@/lib/query-options';
import { useMetaPagePicker, useMetaPixelPicker } from '@/hooks/useMetaAssetPicker';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import metaLogo from '/meta.png';

interface MetaAdAccountSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const MetaAdAccountSelectionModal = ({ isOpen, onClose }: MetaAdAccountSelectionModalProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // ``enabled: isOpen`` so the query only fires when the user actually opens the modal — keeps the
  // navigation popover lightweight.
  const { data: status, isLoading } = useQuery({
    ...metaIntegrationStatusQueryOptions(),
    enabled: isOpen,
  });
  const selectMutation = useSelectMetaAdAccountMutation();
  const queryClient = useQueryClient();

  // 2-step flow (#5977): 'account' → 'setup' (Page + Pixel). Re-opening always starts at the
  // account step — this is an account picker first, whatever state the last visit ended in.
  const [step, setStep] = useState<'account' | 'setup'>('account');
  useEffect(() => {
    if (isOpen) setStep('account');
  }, [isOpen]);

  const handleSelect = async (metaAdAccountId: number) => {
    try {
      // Warm step 2's lists in parallel with the switch: pages/pixels are keyed by the TARGET
      // account id (not the currently-selected one), so they can load while the status refetch
      // runs — step 2 then opens populated instead of on a Graph API round trip (#6179).
      void queryClient.prefetchQuery(metaPixelsQueryOptions(metaAdAccountId));
      if ((status?.scopes ?? []).includes('pages_show_list')) {
        void queryClient.prefetchQuery(metaPagesQueryOptions(metaAdAccountId));
      }
      // mutateAsync resolves only after the status query reflects the new account (the mutation
      // awaits that invalidation), so CONTINUE — disabled while pending — can never reach step 2
      // with the previous account's Page/Pixel (#6179).
      await selectMutation.mutateAsync(metaAdAccountId);
      // No success toast (house rule); the row's Active badge is the confirmation. The modal
      // stays open — CONTINUE advances to the Page + Pixel step (#5977).
    } catch (error) {
      toast({
        title: 'Could Not Switch Ad Account',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleGoToIntegrations = () => {
    onClose();
    navigate({ to: '/settings/integrations' });
  };

  const adAccounts = status?.ad_accounts ?? [];
  const selectedId = status?.selected_ad_account?.id;
  const isConnected = !!status?.is_connected;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-lg shadow-lg">
        <DialogHeader className="p-2">
          <DialogTitle className="text-2xl font-semibold flex items-center gap-2">
            <img src={metaLogo} alt="Meta" className="w-6 h-auto" />
            Meta Ads Account
          </DialogTitle>
          <DialogDescription>
            {step === 'account'
              ? 'Select which Meta ad account this organization uses when creating Meta campaigns.'
              : 'Set the Facebook Page and Meta Pixel this ad account uses for Meta campaigns.'}
          </DialogDescription>
        </DialogHeader>
        <Card className="border-none shadow-none">
          <CardContent className="p-2 space-y-6 rounded-xl">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner size="md" className="text-gray-400 mr-2" />
                <p className="text-gray-500">Loading Meta ad accounts…</p>
              </div>
            ) : !isConnected ? (
              <EmptyState
                title="Meta Not Connected"
                description="Connect a Meta Business account in Settings → Integrations to pick an ad account here."
                action={
                  <Button onClick={handleGoToIntegrations} className="bg-[#290BD9] hover:bg-[#2309C5] text-white">
                    Go to Integrations
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                }
              />
            ) : adAccounts.length === 0 ? (
              <EmptyState
                title="No Meta Ad Accounts"
                description="The connected Meta identity has no ad accounts. Refresh the connection in Settings → Integrations."
                action={
                  <Button onClick={handleGoToIntegrations} className="bg-[#290BD9] hover:bg-[#2309C5] text-white">
                    Go to Integrations
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                }
              />
            ) : step === 'account' ? (
              <>
                <div className="bg-slate-100 rounded-xl p-2 max-h-[60vh] overflow-y-auto space-y-1">
                  {adAccounts.map((account) => {
                    const isSelected = account.id != null && account.id === selectedId;
                    const isLocalAccount = account.id != null;
                    const isPending = selectMutation.isPending && selectMutation.variables === account.id;
                    return (
                      <button
                        key={`${account.account_id}-${account.id ?? 'remote'}`}
                        type="button"
                        disabled={!isLocalAccount || isPending || isSelected}
                        onClick={() => account.id != null && handleSelect(account.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                          isSelected
                            ? 'bg-white ring-1 ring-[#290BD9]'
                            : isLocalAccount
                              ? 'bg-white hover:bg-gray-50'
                              : 'bg-white opacity-60 cursor-not-allowed'
                        }`}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center">
                          <img src={metaLogo} alt="Meta" className="w-4 h-auto" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {account.name || 'Unnamed account'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {account.account_id}
                            {account.currency ? ` · ${account.currency}` : ''}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="flex items-center gap-1 text-xs font-medium text-[#047857]">
                            <CheckCircle className="h-4 w-4" />
                            Active
                          </div>
                        )}
                        {isPending && <Spinner size="sm" className="text-[#290BD9]" />}
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-end">
                  <Button
                    disabled={selectedId == null || selectMutation.isPending}
                    onClick={() => setStep('setup')}
                    className="bg-[#290BD9] hover:bg-[#2309C5] flex items-center gap-2"
                  >
                    CONTINUE
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                {status?.selected_ad_account?.id != null && (
                  <>
                    {/* Which account this step configures (#6182): a failed switch (e.g. the #6007
                        currency guard) leaves the PREVIOUS selection active, and without this line
                        step 2 silently showed that account's Pages/Pixels — reading as "every
                        account is broken" when several switches in a row were rejected. */}
                    <p className="text-sm text-gray-700">
                      Configuring{' '}
                      <span className="font-medium text-gray-900">
                        {status.selected_ad_account.name || 'Unnamed account'}
                      </span>
                      <span className="text-gray-500">
                        {' '}
                        · {status.selected_ad_account.account_id}
                        {status.selected_ad_account.currency ? ` · ${status.selected_ad_account.currency}` : ''}
                      </span>
                    </p>
                    <MetaPagePickerSection
                      metaAdAccountId={status.selected_ad_account.id}
                      currentPageId={status.selected_ad_account.page_id ?? null}
                      currentPageName={status.selected_ad_account.page_name ?? null}
                      hasPagesScope={(status.scopes ?? []).includes('pages_show_list')}
                      onReconnect={handleGoToIntegrations}
                    />
                    <MetaPixelPickerSection
                      metaAdAccountId={status.selected_ad_account.id}
                      currentPixelId={status.selected_ad_account.pixel_id ?? null}
                      currentPixelName={status.selected_ad_account.pixel_name ?? null}
                    />
                  </>
                )}
                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setStep('account')}
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900 border-gray-200 bg-white hover:bg-gray-50"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    BACK
                  </Button>
                  <Button onClick={onClose} className="bg-[#290BD9] hover:bg-[#2309C5] flex items-center gap-2">
                    CONTINUE
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};


/**
 * Facebook Page picker for the selected Meta ad account. Mirrors the wizard-inline picker in
 * ``routes/_authenticated/new/_layout/campaign/meta.tsx`` but lives in the canonical Settings
 * surface (#4277 Chunk 3d): selecting a page here writes ``page_id``/``page_name`` to the
 * ``MetaAdAccount`` row and invalidates the integration-status query so the wizard reads the
 * fresh value on its next render.
 */
function MetaPagePickerSection({
  metaAdAccountId,
  currentPageId,
  currentPageName,
  hasPagesScope,
  onReconnect,
}: {
  metaAdAccountId: number;
  currentPageId: string | null;
  currentPageName: string | null;
  /** ``true`` iff the connected token carries the ``pages_show_list`` scope (added in #4277 chunk 3b). */
  hasPagesScope: boolean;
  /** Routes the user back to Settings → Integrations where they can disconnect + reconnect. */
  onReconnect: () => void;
}) {
  const { pages, isLoading, isPending, onChange: handlePageChange } = useMetaPagePicker({
    metaAdAccountId,
    currentPageId,
    hasPagesScope,
  });

  return (
    <div className="mt-2 p-4 border rounded-lg bg-white space-y-3">
      <div>
        <Label className="text-sm font-medium text-gray-900">Facebook Page</Label>
        <p className="text-xs text-gray-500 mt-1">
          New Meta campaigns will run from this Page. Required before any Meta campaign can launch.
        </p>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner size="sm" />
          Loading Pages…
        </div>
      ) : !hasPagesScope ? (
        // Most common reason ``/me/accounts`` returns empty for an existing user: their stored token
        // predates the ``pages_show_list`` scope being added (chunk 3b). The "Add a Page in Business
        // Manager" message is misleading in that case — surface the real fix.
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Your Meta connection doesn&apos;t include Page access. Disconnect Meta in Settings → Integrations
            and reconnect to grant the missing permission.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={onReconnect}
            className="text-[#290BD9] border-[#290BD9] hover:bg-[#290BD9]/5"
          >
            Open Integrations
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      ) : pages.length === 0 ? (
        <p className="text-sm text-gray-700">
          No Facebook Pages found on this account. Add one in Meta Business Manager, then reopen this dialog.
        </p>
      ) : (
        <MetaAssetSelect
          value={currentPageId}
          onValueChange={handlePageChange}
          disabled={isPending}
          placeholder={currentPageName ?? 'Select a Facebook Page'}
          triggerClassName="h-9 text-gray-900"
          options={pages.map((page) => ({
            value: page.id,
            label: (
              <>
                {page.name}
                {page.category ? <span className="text-gray-500"> · {page.category}</span> : null}
              </>
            ),
          }))}
        />
      )}
    </div>
  );
}

/**
 * Meta Pixel picker for the selected ad account (Phase 7 Chunk 0). Mirrors
 * :func:`MetaPagePickerSection`: selecting a Pixel writes ``pixel_id``/``pixel_name`` to the
 * ``MetaAdAccount`` row and invalidates the integration-status query. The bound Pixel drives the
 * conversions ``promoted_object`` — a conversions-objective (OUTCOME_LEADS) campaign won't create
 * until one is set. No scope branch (unlike Pages): Pixels read via the always-granted
 * ``ads_management`` / ``ads_read`` scopes.
 */
function MetaPixelPickerSection({
  metaAdAccountId,
  currentPixelId,
  currentPixelName,
}: {
  metaAdAccountId: number;
  currentPixelId: string | null;
  currentPixelName: string | null;
}) {
  const { pixels, isLoading, isPending, onChange: handlePixelChange } = useMetaPixelPicker({
    metaAdAccountId,
    currentPixelId,
  });

  return (
    <div className="mt-2 p-4 border rounded-lg bg-white space-y-3">
      <div>
        <Label className="text-sm font-medium text-gray-900">Meta Pixel</Label>
        <p className="text-xs text-gray-500 mt-1">
          New lead campaigns optimize toward conversions on this Pixel. Required before a lead campaign can launch.
        </p>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Spinner size="sm" />
          Loading Pixels…
        </div>
      ) : pixels.length === 0 ? (
        <p className="text-sm text-gray-700">
          No Meta Pixels found on this account. Create one in Meta Events Manager, then reopen this dialog.
        </p>
      ) : (
        <MetaAssetSelect
          value={currentPixelId}
          onValueChange={handlePixelChange}
          disabled={isPending}
          placeholder={currentPixelName ?? 'Select a Meta Pixel'}
          triggerClassName="h-9 text-gray-900"
          options={pixels.map((pixel) => ({
            value: pixel.id,
            label: (
              <>
                {pixel.name || 'Unnamed Pixel'}
                <span className="text-gray-500"> · {pixel.id}</span>
              </>
            ),
          }))}
        />
      )}
    </div>
  );
}
